import _ from '@sparta.global.vendors/lodash';

const Utilities = {
  /**
   * Generates a random hex string to be used for token generation
   * @return {string} random hex string
  */
  uuidString: () => ((1 + Math.random()) * 0x10000 || 0).toString(16).substring(1)
};

/* global _ Utilities */

// Regex for escaping event names.
const regex = /[(.)+([)+(\])+]/gi;

// Replacement characters for regex matches.
const replacementChars = {
  '.': '/',
  '[': '(',
  ']': ')'
};
const EVENTS = {
  SPA_PARAMS_LOADED: 'SPA_PARAMS_LOADED.spartaEventsUtility',
  APP_CONTAINER_LOADED: 'APP_CONTAINER_LOADED.spartaEventsUtility',
  APP_CONTEXT_LOADED: 'APP_CONTEXT_LOADED.spartaEventsUtility'
};

/**
 * Pub-sub for app events. Singleton.
 */
class SpartaEventsUtility {
  /**
   * Sets up an instance of the utility.
   */
  constructor() {
    this.subscriptions = {};
    this.alreadyBeenPublished = {};
  }

  /**
   * Returns the events object.
   * @return {object} Events object.
   */
  get EVENTS() {
    return EVENTS;
  }

  /**
   * Listens for a custom event.
   * @param {string} event - This can be namespaced with a `:` like
   *   `SPARTA_GLOBAL_READY.spartaBootstrapUtility:foo`.
   * @param {function} callback - A fuction to be executed upon completion of the event.
   * @param {boolean} retroactive - Whether to trigger immediate callback when the event has already
   *   been published before `sub`.
   * @return {string|null} A token used for unsubscribing, or `null` if passed an invalid `event`.
   */
  sub(event, callback = () => {}, retroactive = false, widgetInfo) {
    if (typeof event !== 'string') {
      return null;
    }
    if (widgetInfo && widgetInfo.id) {
      event = `${event}_${widgetInfo.id}`;
    }
    const [eventName, nameSpace = 'default'] = event.split(':');
    const escapedEventName = eventName.replace(regex, matched => replacementChars[matched]);
    const nameSpaces = nameSpace.replaceAll(regex, matched => replacementChars[matched]);
    const subPath = _.get(this.subscriptions, `${escapedEventName}[${nameSpaces}]`, null);
    const token = `${event}_$_${Utilities.uuidString()}`;
    if (!subPath) {
      // Test namespace for special characters
      let nameSpaceNiceName = nameSpaces;
      if (nameSpaces.includes('.')) {
        nameSpaceNiceName = `"${nameSpaceNiceName.replaceAll('"', '\\"')}"`;
      }
      // Set nameSpace
      _.set(this.subscriptions, `${escapedEventName}[${nameSpaceNiceName}]`, []);
    }
    this.subscriptions[`${escapedEventName}`][nameSpaces].push({
      callback,
      token
    });
    if (retroactive && this.alreadyBeenPublished[`${escapedEventName}`] && this.alreadyBeenPublished[`${escapedEventName}`].published) {
      const args = this.alreadyBeenPublished[`${escapedEventName}`].data;
      callback({
        event,
        args,
        data: args
      });
    }
    return token;
  }

  /**
   * @param {string} event - This can be namespaced with a `:` like
   *   `SPARTA_GLOBAL_READY.spartaBootstrapUtility:foo`.
   * @returns {void}
   */
  unsubByEvent(event, widgetInfo) {
    if (!event || typeof event !== 'string') {
      return;
    }
    if (widgetInfo && widgetInfo.id) {
      event = `${event}_${widgetInfo.id}`;
    }
    const [eventName, nameSpace] = event.split(':');
    const escapedEventName = eventName.replace(regex, matched => replacementChars[matched]);
    if (this.subscriptions[escapedEventName]) {
      if (!nameSpace) {
        delete this.subscriptions[escapedEventName];
      } else {
        delete this.subscriptions[escapedEventName][nameSpace];
      }
    }
  }

  /**
   * Unsubscribes (removes a listener) from the event registered with the provided token.
   * @param {string} token - The token for the event to unsubscribe from.
   * @return {boolean|null} Returns `false` if no events are found matching the token, `true` if
   *   the event was removed, or `null` if passed an invalid token.
   */
  unsubByToken(token) {
    if (typeof token !== 'string' || !token || !token.includes('_$_')) {
      return null;
    }
    const [eventName, nameSpace = 'default'] = token.split('_$_')[0].split(':');
    const escapedEventName = eventName.replace(regex, matched => replacementChars[matched]);
    const subscriptions = _.get(this.subscriptions, `${escapedEventName}[${nameSpace}]`, []);
    return subscriptions.some((sub, idx) => {
      if (sub.token === token) {
        subscriptions.splice(idx, 1);
        return true;
      }
      return false;
    });
  }

  /**
   * Unsubscribes (removes a listener) from the event registered with the provided widget token
   * signature.
   * @param {string} token - The token for the event to unsubscribe from.
   * @return {boolean|null} Returns `false` if no events are found matching the token, `true` if
   *   the event was removed, or `null` if passed an invalid token.
   */
  unsubWidgetByToken(token) {
    if (typeof token !== 'string' || !token || !token.includes('_$_')) {
      return null;
    }
    const eventName = token.substring(0, token.lastIndexOf('_$_')).replace('.', '/');
    const escapedEventName = eventName.replace(regex, matched => replacementChars[matched]);
    const subscriptions = _.get(this.subscriptions, `${escapedEventName}[default]`, []);
    return subscriptions.some((sub, idx) => {
      if (sub.token === token) {
        subscriptions.splice(idx, 1);
        return true;
      }
      return false;
    });
  }

  /**
   * Unsubscribes (removes listeners) from the event registered with the provided event name pattern
   * @param {string} eventPattern - Pattern of the event name to unsubscribe.
   * @return {boolean|null} Returns `false` if no events are found matching the token, `true` if
   *   the event was removed, or `null` if passed an invalid token.
   */
  unsubByEventNamePattern(eventPattern) {
    let deleted = false;
    if (!eventPattern) {
      return null;
    }
    const regex = new RegExp(eventPattern);
    for (const eventName in this.subscriptions) {
      // event name match
      if (eventName.match(regex)) {
        delete this.subscriptions[eventName];
        deleted = true;
      }

      // namespace event name match
      for (const nameSpace in this.subscriptions[eventName]) {
        if (typeof namespace === 'string' && nameSpace === 'default') continue;
        if (nameSpace.match(regex)) {
          delete this.subscriptions[eventName][nameSpace];
          deleted = true;
        }
      }
    }
    return deleted;
  }

  /**
   * @param {string} event - This can be namespaced with a `:` like
   *   `SPARTA_GLOBAL_READY.spartaBootstrapUtility:foo`.
   * @param {*} data - Any data to be passed to the callback.
   * @returns {void}
   */
  pub(event, data, widgetInfo) {
    if (typeof event !== 'string') {
      return;
    }
    if (widgetInfo && widgetInfo.id) {
      event = `${event}_${widgetInfo.id}`;
    }

    /****** Dispatching a custom event is not required if we are using spartaEventsUtility throughout the application ******/
    // try {
    //   document.dispatchEvent(new CustomEvent(event, { detail: data }));
    // } catch (e) {
    //   // Fail silently.
    // }

    const [eventName, nameSpace] = event.split(':');
    const escapedEventName = eventName.replace(regex, matched => replacementChars[matched]);
    let subscriptions = _.get(this.subscriptions, escapedEventName, {});
    if (nameSpace) {
      const nameSpaces = nameSpace.replaceAll(regex, matched => replacementChars[matched]);
      subscriptions = _.get(subscriptions, nameSpaces, {});
    }
    let callbackMaps = [];
    Object.keys(subscriptions).forEach(sub => {
      callbackMaps = callbackMaps.concat(subscriptions[sub]);
    });
    this.alreadyBeenPublished[escapedEventName] = {
      published: true,
      data
    };
    callbackMaps.forEach(obj => {
      obj.callback({
        event,
        data
      });
    });
  }
}
var index = new SpartaEventsUtility();

export { index as default };
